import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import clsx from 'clsx';
import { useBrainStore } from '@/state/brainStore';
import type { StudioId } from '@/domain/types';
import type { ReportingScope } from '@/domain/reportingCompare';
import { perStudioSeries, singleStudioScope } from '@/domain/reportingCompare';
import { Figure, Money } from '@/components/reporting/Money';
import InfoTip from '@/components/InfoTip';
import { moneyAtRisk } from '@/domain/reportingMoney';
import {
  leverFunnels,
  businessLeaks,
  weeksBookedAhead,
  reportingNow,
  type LeakPathStep,
  type LeakCategory,
} from '@/domain/reportingEngine';

function leakFor(days: readonly string[], sid: StudioId | undefined) {
  const leaks = businessLeaks(days, sid);
  const levers = leverFunnels(days, sid);
  const weeks = weeksBookedAhead(sid);
  const risk = moneyAtRisk(days, sid, reportingNow());
  return { leaks, levers, weeks, risk };
}

// Which lever a leak category maps to, for the chase-list filter chips.
const CATEGORY_LEVER: Record<string, 'deposits' | 'rebooks' | 'confirmations'> = {
  'reschedule-leaked': 'deposits',
  'deposit-no-date': 'deposits',
  'no-show': 'confirmations',
  'declined-in-chair': 'rebooks',
  'deferred-unresolved': 'rebooks',
  'will-rebook-later': 'rebooks',
};

const LEVER_CHIPS = [
  { key: 'all', label: 'All' },
  { key: 'deposits', label: 'Deposits' },
  { key: 'rebooks', label: 'Rebooks' },
  { key: 'confirmations', label: 'Confirmations' },
] as const;

/** The Leak tab: where the business is leaking this week, and who to chase. */
export default function LeakTab({ scope }: { scope: ReportingScope }) {
  const bookings = useBrainStore((s) => s.bookings);
  const deposits = useBrainStore((s) => s.deposits);
  const customers = useBrainStore((s) => s.customers);
  const confirmationCalls = useBrainStore((s) => s.confirmationCalls);
  const artists = useBrainStore((s) => s.artists);
  const agents = useBrainStore((s) => s.agents);
  const studios = useBrainStore((s) => s.studios);
  const pieces = useBrainStore((s) => s.pieces);
  const { days, studioIds, compareMode, moneyVisible } = scope;

  const [leverFilter, setLeverFilter] = useState<string>('all');

  const single = singleStudioScope(studioIds);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const one = useMemo(
    () => leakFor(days, single),
    [days, single, bookings, deposits, customers, confirmationCalls, artists, agents, pieces],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const series = useMemo(
    () => (compareMode ? perStudioSeries(studioIds, (sid) => moneyAtRisk(days, sid, reportingNow())) : []),
    [compareMode, studioIds, days, bookings, deposits, customers, agents, confirmationCalls, artists, pieces],
  );

  if (compareMode) {
    return (
      <div className="space-y-4 max-w-6xl" data-testid="reporting-leak">
        <SectionLabel tone="blood">The leak · studios compared</SectionLabel>
        <div className="card overflow-x-auto">
          <table className="w-full text-sm" data-testid="leak-compare-table">
            <thead>
              <tr>
                <th className="text-left label-small-caps pb-2">Studio</th>
                {series.map((s) => (
                  <th key={s.studioId} className="text-right label-small-caps pb-2">
                    {studios[s.studioId]?.name ?? s.studioId}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-gold-dim/15">
                <td className="py-2 text-cream-soft">Appointments at risk</td>
                {series.map((s) => (
                  <td key={s.studioId} className="py-2 text-right font-mono text-cream">
                    {s.value.totalCount.toLocaleString('en-AU')}
                  </td>
                ))}
              </tr>
              <tr className="border-t border-gold-dim/15">
                <td className="py-2 text-cream-soft">Money at risk</td>
                {series.map((s) => (
                  <td key={s.studioId} className="py-2 text-right font-mono text-blood">
                    <Money amountAud={s.value.totalMoneyAud} visible={moneyVisible} />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  const { leaks, levers, weeks, risk } = one;
  const riskPart = Object.fromEntries(risk.parts.map((p) => [p.key, p]));
  const byKey = Object.fromEntries(levers.map((l) => [l.key, l]));
  const conf = byKey['confirmations'];
  const reb = byKey['rebooks'];
  const dep = byKey['deposits'];

  // Flatten every slip into one chase list, tagged with its lever, highest value first.
  const allRecords = leaks.categories.flatMap((cat) =>
    cat.records.map((rec) => ({ rec, cat })),
  );
  const filtered = allRecords
    .filter(({ cat }) => leverFilter === 'all' || CATEGORY_LEVER[cat.key] === leverFilter)
    .sort((a, b) => b.rec.valueAtRiskAud - a.rec.valueAtRiskAud);
  const filteredValue = filtered.reduce((sum, { rec }) => sum + rec.valueAtRiskAud, 0);

  return (
    <div className="space-y-8 max-w-6xl" data-testid="reporting-leak">
      {/* Intro block */}
      <IntroBlock
        answers="Where is the business leaking this week — and who should be chasing it?"
        powers="The Wednesday review, and today's chase list — assigned to the person who last touched each client."
      />

      {/* Hero tiles */}
      <section className="space-y-3">
        <SectionLabel tone="blood">The leak · this window</SectionLabel>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <HeroTile
            label="Money at risk"
            tone="blood"
            info="Deposit money already taken on each record at risk: unconfirmed seats this window + sessions this window with no rebook + deposits on file with no date (now). The seat value line is what those bookings are worth."
            value={
              <Figure testId="leak-risk-total" amountAud={risk.totalMoneyAud} count={risk.totalCount}>
                <Money amountAud={risk.totalMoneyAud} visible={moneyVisible} />
              </Figure>
            }
            tint
            sub={
              <>
                {risk.parts.map((p, i) => (
                  <span key={p.key}>
                    {i > 0 ? ' + ' : ''}
                    <Figure testId={`leak-risk-part-${p.key}`} amountAud={p.moneyAud} count={p.count}>
                      <Money amountAud={p.moneyAud} visible={moneyVisible} />
                    </Figure>{' '}
                    {p.label.toLowerCase()}
                  </span>
                ))}
                <span className="block mt-1 text-cream-faint">
                  Seat value{' '}
                  <Figure testId="leak-risk-seat-total" amountAud={risk.totalSeatAud}>
                    <Money amountAud={risk.totalSeatAud} visible={moneyVisible} />
                  </Figure>
                </span>
              </>
            }
          />
          <HeroTile
            label="Weeks booked ahead"
            value={
              <>
                {weeks.weeks.toFixed(1)} <span className="text-cream-faint text-xl">wk</span>
              </>
            }
            sub={`Target ≥ ${Math.round(weeks.thresholdPct * 100)}% utilisation before the first free week`}
            info="Whole weeks each studio is booked at/above the utilisation target before the first week with free space."
          />
          <HeroTile
            label="Appointments at risk"
            value={risk.totalCount.toLocaleString('en-AU')}
            sub={`${riskPart['confirmations']?.count ?? 0} unconfirmed · ${riskPart['rebooks']?.count ?? 0} no rebook · ${riskPart['deposits']?.count ?? 0} on file, no date`}
          />
        </div>
      </section>

      {/* The leak-flow funnel — the signature visual */}
      <section className="card p-6">
        <div className="flex items-baseline justify-between gap-4 flex-wrap mb-4">
          <h2 className="font-serif text-xl text-cream">Where the week leaks</h2>
          <p className="font-inter text-[11px] text-cream-faint">
            In play → confirmations, then rebooks → undated deposits are the downstream fallout
          </p>
        </div>
        <LeakFunnel conf={conf} reb={reb} dep={dep} moneyVisible={moneyVisible} />
        <div className="flex items-center gap-5 mt-4">
          <Legend swatch="bg-gold-dim" label="Survives the gate" />
          <Legend swatch="bg-blood" label="Leaks out here" />
        </div>
      </section>

      {/* The three levers */}
      <section className="space-y-3">
        <SectionLabel description="one number each; the slips themselves sit in the chase list below.">
          The three levers
        </SectionLabel>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[conf, reb, dep].filter((lever): lever is NonNullable<Lever> => Boolean(lever)).map((lever) => (
            <div key={lever.key} className="card p-5" data-testid="lever-card">
              <p className="font-serif text-lg text-cream">{lever.label}</p>
              <p className="font-serif text-5xl text-blood mt-1 leading-none">
                {lever.ratePct === null ? '—' : `${Math.round(lever.ratePct)}%`}
              </p>
              <p className="font-inter text-xs text-cream-soft mt-3">
                {lever.missed.toLocaleString('en-AU')} of {lever.total.toLocaleString('en-AU')} missed ·{' '}
                <Money amountAud={lever.valueAtRiskAud} visible={moneyVisible} /> at risk
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* The chase list */}
      <section className="space-y-3">
        <SectionLabel description="open the list for every slip, its path, and who last touched it.">
          Where the business leaks
        </SectionLabel>
        <div className="card overflow-hidden">
          <div className="flex items-baseline justify-between gap-3 px-5 py-4 border-b border-gold-dim/15 flex-wrap">
            <div className="flex items-baseline gap-3 flex-wrap">
              <h3 className="font-serif text-lg text-cream">The chase list</h3>
              <span className="font-inter text-xs text-cream-faint">every slip that needs a call today</span>
            </div>
            <span className="font-mono text-sm text-cream-soft">
              <span className="text-blood"><Money amountAud={filteredValue} visible={moneyVisible} /></span>
              {' · '}
              {filtered.length} records · highest-value first
            </span>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 px-5 py-3 border-b border-gold-dim/15 flex-wrap">
            <span className="label-small-caps text-cream-faint mr-1">Lever</span>
            {LEVER_CHIPS.map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={() => setLeverFilter(chip.key)}
                data-testid={`leak-lever-chip-${chip.key}`}
                className={clsx(
                  'rounded-full px-3 py-1 text-xs font-inter transition border',
                  leverFilter === chip.key
                    ? 'bg-gold/15 text-gold-bright border-gold-dim/60'
                    : 'text-cream-soft border-gold-dim/25 hover:text-gold-bright',
                )}
              >
                {chip.label}
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="leak-chase-list">
              <thead>
                <tr className="border-b border-gold-dim/15">
                  <th className="label-small-caps text-cream-faint font-normal px-5 py-2">Client</th>
                  <th className="label-small-caps text-cream-faint font-normal px-3 py-2">Lever / leak point</th>
                  <th className="label-small-caps text-cream-faint font-normal px-3 py-2">The path it took</th>
                  <th className="label-small-caps text-cream-faint font-normal px-3 py-2 text-right">At risk</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-5 py-6 text-center font-serif text-cream-soft">
                      Nothing leaking on this lever — clean week.
                    </td>
                  </tr>
                ) : (
                  filtered.map(({ rec, cat }) => (
                    <tr key={rec.id} className="border-b border-gold-dim/10 align-top hover:bg-gold/5">
                      <td className="px-5 py-3">
                        <div className="text-cream">{rec.name}</div>
                        <div className="text-cream-faint text-xs mt-0.5">{rec.subtitle}</div>
                      </td>
                      <td className="px-3 py-3">
                        <LeverTag category={cat} />
                      </td>
                      <td className="px-3 py-3 max-w-md">
                        <PathSteps steps={rec.path} />
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-blood whitespace-nowrap">
                        <Money amountAud={rec.valueAtRiskAud} visible={moneyVisible} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Footnote */}
      <p className="font-inter text-xs text-cream-faint border-t border-gold-dim/15 pt-4">
        <span className="pill-cream mr-2">Audience aware</span>
        Owner sees every dollar. Switch to the Operations lens and money vanishes — Georgina gets flow and counts, no
        revenue. Artists never see this page; they see their own counts and streaks.
      </p>
    </div>
  );
}

// ---- sub-components ----

function SectionLabel({
  children,
  description,
  tone = 'gold',
}: {
  children: ReactNode;
  description?: string;
  tone?: 'gold' | 'blood';
}) {
  return (
    <p className="label-small-caps">
      <span className={tone === 'blood' ? 'text-blood' : 'text-gold'}>{children}</span>
      {description ? <span className="text-cream-faint normal-case tracking-normal font-normal"> — {description}</span> : null}
    </p>
  );
}

function IntroBlock({ answers, powers }: { answers: string; powers: string }) {
  return (
    <section className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-2">
      <div>
        <p className="label-small-caps text-cream-faint mb-1">This tab answers</p>
        <p className="font-serif italic text-cream-soft">&ldquo;{answers}&rdquo;</p>
      </div>
      <div>
        <p className="label-small-caps text-cream-faint mb-1">Decision it powers</p>
        <p className="font-serif text-cream">{powers}</p>
      </div>
    </section>
  );
}

function HeroTile({
  label,
  value,
  sub,
  info,
  tone,
  tint,
}: {
  label: string;
  value: ReactNode;
  sub: ReactNode;
  info?: string;
  tone?: 'blood';
  tint?: boolean;
}) {
  return (
    <div className={clsx('card p-5', tint && 'bg-blood/[0.04] border-blood/20')}>
      <p className="label-small-caps flex items-center gap-1">
        {label}
        {info ? <InfoTip>{info}</InfoTip> : null}
      </p>
      <p className={clsx('font-serif text-4xl mt-2 leading-none', tone === 'blood' ? 'text-blood' : 'text-cream')}>
        {value}
      </p>
      <p className="font-inter text-xs text-cream-soft mt-3">{sub}</p>
    </div>
  );
}

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5 font-inter text-[11px] text-cream-soft">
      <span className={clsx('inline-block w-3 h-3 rounded-sm', swatch)} />
      {label}
    </span>
  );
}

const TAG_STYLE: Record<string, string> = {
  deposits: 'border-gold-dim/50 bg-gold/10 text-gold',
  rebooks: 'border-blood/50 bg-blood/10 text-blood',
  confirmations: 'border-azure/50 bg-azure/10 text-azure',
};

function LeverTag({ category }: { category: LeakCategory }) {
  const lever = CATEGORY_LEVER[category.key] ?? 'deposits';
  return (
    <div>
      <span
        className={clsx(
          'inline-flex rounded-sm border px-2 py-0.5 text-[10px] font-inter uppercase tracking-widest',
          TAG_STYLE[lever],
        )}
      >
        {lever}
      </span>
      <div className="text-cream-faint text-xs mt-1">{category.label}</div>
    </div>
  );
}

function PathSteps({ steps }: { steps: LeakPathStep[] }) {
  return (
    <span className="font-inter text-xs text-cream-soft leading-relaxed">
      {steps.map((step, idx) => (
        <span key={idx}>
          {idx > 0 ? <span className="text-cream-faint"> → </span> : null}
          <span className={step.leak ? 'text-blood' : undefined}>{step.label}</span>
          {step.detail ? <span className="text-cream-faint"> ({step.detail})</span> : null}
        </span>
      ))}
    </span>
  );
}

// The tapering leak-flow funnel: a gold area that narrows as it passes three
// gates, with a red drop-out box under each. Survival is the running product of
// each lever's pass-rate — a normalised cascade (exact 100-normalised figures are
// a follow-up); every gate's drop count + $ is the real lever number.
type Lever = ReturnType<typeof leverFunnels>[number] | undefined;

function LeakFunnel({
  conf,
  reb,
  dep,
  moneyVisible,
}: {
  conf: Lever;
  reb: Lever;
  dep: Lever;
  moneyVisible: boolean;
}) {
  const W = 1000;
  const H = 220;
  const passRate = (l: Lever) => (l && l.total > 0 ? l.converted / l.total : 1);
  const h0 = 0.92;
  const s1 = h0 * passRate(conf);
  const s2 = s1 * passRate(reb);
  const s3 = s2 * passRate(dep);
  const y = (frac: number) => H - frac * (H - 24);

  // gate x positions
  const gates = [
    { x: 0.26, label: 'Confirmations', lever: conf, top: s1 },
    { x: 0.56, label: 'Rebooks', lever: reb, top: s2 },
    { x: 0.83, label: 'Deposits', lever: dep, top: s3 },
  ];
  const pts = [
    [0, y(h0)],
    [gates[0]!.x * W, y(s1)],
    [gates[1]!.x * W, y(s2)],
    [gates[2]!.x * W, y(s3)],
    [W, y(s3)],
  ];
  const area = `M0,${H} ${pts.map(([px, py]) => `L${px},${py}`).join(' ')} L${W},${H} Z`;
  const endPct = Math.round(s3 * 100);

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H + 44}`} className="w-full min-w-[560px]" role="img" aria-label="Leak flow funnel">
        <defs>
          <linearGradient id="leakfill" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="rgb(var(--accent-gold-dim))" stopOpacity="0.55" />
            <stop offset="1" stopColor="rgb(var(--accent-gold-dim))" stopOpacity="0.28" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#leakfill)" />
        {/* start / end labels */}
        <text x="4" y={H - 6} className="fill-[rgb(var(--text-soft))]" fontSize="13" fontFamily="Inter">
          100 in play
        </text>
        <text x={W - 4} y={y(s3) - 10} textAnchor="end" fontSize="14" fontWeight="600" fontFamily="Lora, serif" className="fill-[rgb(var(--accent-gold))]">
          {endPct} compounding forward
        </text>
        {gates.map((g) => {
          const gx = g.x * W;
          const missed = g.lever?.missed ?? 0;
          return (
            <g key={g.label}>
              <line x1={gx} y1={16} x2={gx} y2={H} stroke="rgb(var(--accent-gold-dim))" strokeDasharray="4 4" strokeOpacity="0.7" />
              <text x={gx} y={12} textAnchor="middle" fontSize="13" fontWeight="700" letterSpacing="1.5" fontFamily="Inter" className="fill-[rgb(var(--text-strong))]">
                {g.label.toUpperCase()}
              </text>
              {/* drop-out box */}
              <rect x={gx - 26} y={y(g.top) + 6} width="52" height="26" rx="5" fill="rgb(var(--accent-blood))" fillOpacity="0.85" />
              {g.lever && g.lever.valueAtRiskAud > 0 && moneyVisible ? (
                <text x={gx} y={y(g.top) + 52} textAnchor="middle" fontSize="13" fontWeight="600" fontFamily="Lora, serif" className="fill-[rgb(var(--accent-blood))]">
                  ${Math.round(g.lever.valueAtRiskAud / 100) / 10}k
                </text>
              ) : null}
              <text x={gx} y={y(g.top) + (moneyVisible && g.lever && g.lever.valueAtRiskAud > 0 ? 70 : 52)} textAnchor="middle" fontSize="11" fontFamily="Inter" className="fill-[rgb(var(--text-faint))]">
                {missed} {g.label === 'Confirmations' ? 'unconfirmed' : g.label === 'Rebooks' ? 'no rebook' : 'undated'}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
